import { Preferences } from "@capacitor/preferences";
import { isNativeApp } from "./platform";

// Offline order-guide snapshot for the native shell.
//
// Written whenever the buyer's menu loads while online. Read from two places:
//  1. mobile/www/offline.html (Capacitor server.errorPath) — the branded page
//     shown on a COLD start with no connectivity. It best-effort reads this
//     key over the bridge (works on iOS; Android errorPath pages have no
//     plugin access, so they show the branded screen without the guide).
//  2. The SPA's own offline banner (warm offline — connection drops while the
//     app is already loaded).
//
// Deliberately tiny: names + units only, capped at 60 items. No prices, no
// images — it is a "what do I usually order" memory aid, not a catalog.

export interface OfflineGuideSnapshot {
  orgName: string;
  items: Array<{ name: string; unit?: string }>;
  savedAt: string; // ISO
}

const KEY = "fo_offline_guide";

export async function saveOrderGuideSnapshot(
  orgName: string,
  products: Array<{ name: string; unit?: string | null }>,
): Promise<void> {
  if (!isNativeApp()) return;
  if (!products.length) return;
  try {
    const snapshot: OfflineGuideSnapshot = {
      orgName,
      items: products.slice(0, 60).map((p) => ({
        name: p.name,
        ...(p.unit ? { unit: p.unit } : {}),
      })),
      savedAt: new Date().toISOString(),
    };
    await Preferences.set({ key: KEY, value: JSON.stringify(snapshot) });
  } catch {
    // best-effort cache — never interfere with the live menu
  }
}

export async function readOrderGuideSnapshot(): Promise<OfflineGuideSnapshot | null> {
  if (!isNativeApp()) return null;
  try {
    const { value } = await Preferences.get({ key: KEY });
    if (!value) return null;
    const parsed = JSON.parse(value);
    if (!parsed || !Array.isArray(parsed.items)) return null;
    return parsed as OfflineGuideSnapshot;
  } catch {
    return null;
  }
}
